import { useMemo, useCallback } from 'react'
import {
    RECORD_TYPE_CHECK,
    RECORD_TYPE_DOCUMENT,
    HUMAN_STATUSES,
} from '@constants'
import dayjs from 'dayjs'
import cn from 'clsx'

import { Link } from 'react-router-dom'
import Highlighter from 'react-highlight-words'
import { BsFillArrowUpRightSquareFill, BsSortUp, BsSortDown } from 'react-icons/bs'
import { formatDate } from '@/utils/date'

import './Partners.css'

const checkRowHeadersMap = {
    name: 'Name',
    fileName: 'File Name',
    organizationId: 'Organization',
    createdAt: 'Created Date',
    reviewStatus: 'Status',
    workDuration: 'Work Duration',
}

const documentRowHeadersMap = {
    name: 'Name',
    fileName: 'File Name',
    collectionId: 'Collection',
    createdAt: 'Created Date',
    status: 'Status',
    workDuration: 'Work Duration',
}

const rowHeadersMaps = {
    [RECORD_TYPE_CHECK]: checkRowHeadersMap,
    [RECORD_TYPE_DOCUMENT]: documentRowHeadersMap,
}

const rowLinksMap = {
    [RECORD_TYPE_CHECK]: '/partners/annotate/check',
    [RECORD_TYPE_DOCUMENT]: '/partners/annotate/document',
}

export const PartnersRecordsTable = ({
    className,
    recordType,
    records,
    orderField,
    orderDirection,
    query,
    onHeaderClick = () => console.warn('onHeaderClick not defined'),
}) => {
    
    const baseUrl = useMemo(() => rowLinksMap[recordType], [recordType])
    
    const rowHeadersMap = useMemo(() => rowHeadersMaps[recordType], [recordType])
    
    const formatDuration = seconds => {
        
        if (!seconds || seconds === 0)
            return '-'
        
        const d = dayjs.duration({ seconds })
        const s = d.seconds()
        const h = d.hours()
        const m = d.minutes()
        
        const v = []
        
        if (s) v.unshift(`${s}s`)
        if (m) v.unshift(`${m}m`)
        if (h) v.unshift(`${h}h`)
        
        return v.join(', ')
        
    }
    
    const statusFor = useCallback(record => {
        
        if (!record) return ''
        
        switch (recordType) {
            case RECORD_TYPE_CHECK: return HUMAN_STATUSES[record.status] ?? record.status
            case RECORD_TYPE_DOCUMENT: return HUMAN_STATUSES[record.orgStatus] ?? record.orgStatus
            default: return ''
        }
        
    }, [recordType])
    
    if (!rowHeadersMap) return null
    
    return (
        
        <table className={cn('PartnersRecordsTable table table-sm table-nowrap table-pin-rows card-table', className)}>
            <thead>
                <tr>
                    {Object.keys(rowHeadersMap).map(it => (
                        <th
                            key={`PartnersRecordsTable-th-${it}`}
                            className="th-no-break"
                            onClick={() => onHeaderClick(it)}>
                            <div className="flex items-center content-center gap-2">
                                <span>{rowHeadersMap[it]}</span>
                                {orderField === it && (
                                    orderDirection === 'asc' ? <BsSortDown /> : <BsSortUp />
                                )}
                            </div>
                        </th>
                    ))}
                    <th>
                        {/* Actions */}
                    </th>
                </tr>
            </thead>
            <tbody className="list">
                {records.map(it => (
                    <tr key={it.id}>
                        <td>
                            <Highlighter
                                highlightClassName="YourHighlightClass"
                                searchWords={[query]}
                                autoEscape={true}
                                textToHighlight={it.name} />
                        </td>
                        <td>
                            <Highlighter
                                highlightClassName="YourHighlightClass"
                                searchWords={[query]}
                                autoEscape={true}
                                textToHighlight={it.fileName} />
                        </td>
                        <td>
                            {recordType === RECORD_TYPE_CHECK && it.organization?.name}
                            {recordType === RECORD_TYPE_DOCUMENT && it.collection?.name}
                        </td>
                        <td>{formatDate(it.createdAt)}</td>
                        <td>{statusFor(it)}</td>
                        <td>{formatDuration(it.annotationWorkDurationSec)}</td>
                        <td>
                            <div className="flex justify-center items-center content-center">
                                <Link
                                    className="flex justify-center items-center content-center"
                                    to={`${baseUrl}/${it.id}`}>
                                    <span>Annotate</span>
                                    <BsFillArrowUpRightSquareFill className="ml-2" />
                                </Link>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        
    )
    
}

export default PartnersRecordsTable
